import { useState } from "react";

import { mpan_search_limit } from "../config/constants";
import { searchMultipleMetersByMpan } from "../services/electralink.service";
import { MultiMpanResult } from "../types/services.types";
// Import the "searchMultipleMetersByMpan" function to fetch meter details for 1+ MPANs.
// Import the maximum number of MPANs allowed per search to implement client-side validation.

// MPAN validation regex: must be exactly 13 digits.
const mpan_regex = /^[0-9]{13}$/;

// Error message for invalid MPAN format to be used in both validation and results display.
const invalid_mpan_format_error_message = "Invalid MPAN format (must be 13 digits)";

const useMpanSearch = () => {
  // Set up state variables for the MPAN search tab to track input, validation, loading, errors, and fetched results.
  const [mpanInput, setMpanInput] = useState<string>("");
  // Client-side validation (i.e., before an API call) so the validation is checked instantly:
  const [validationError, setValidationError] = useState<string | null>(null);
  // Server errors (i.e., after a failed API call).
  const [mpanError, setMpanError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [mpanResults, setMpanResults] = useState<MultiMpanResult[]>([]);

  const handleSearch = async () => {
    // Clear all previous errors and results at the start of a new search.
    setValidationError(null);
    setMpanError(null);
    setMpanResults([]);

    // Parse comma-separated MPANs and remove any whitespace from each MPAN.
    const mpans = mpanInput
      .split(",")
      .map((mpan) => mpan.trim().replace(/\s/g, ""))
      .filter((mpan) => mpan.length > 0);

    // Check if mpanInput is empty.
    if (mpans.length === 0) {
      setValidationError("Please enter at least one MPAN");
      return;
    }

    // Check if it exceeds the limit for multi MPAN search.
    if (mpans.length > mpan_search_limit) {
      setValidationError(
        `There is a maximum ${String(mpan_search_limit)} MPANs allowed per search`,
      );
      return;
    }

    // Separate valid and invalid MPANs based on the regex (must be exactly 13 digits).
    // This allows us to give instant feedback for any invalid MPAN/s formats while still searching for valid ones, rather than blocking the entire search or waiting for the API to validate each MPAN.
    const validMpans = mpans.filter((mpan) => mpan_regex.test(mpan));
    const invalidMpans = mpans.filter((mpan) => !mpan_regex.test(mpan));

    // If ALL MPANs are invalid, display an error.
    if (validMpans.length === 0) {
      setValidationError(`${invalid_mpan_format_error_message}: ${invalidMpans.join(", ")}`);
      return;
    }

    // Display the 'loading' message on the UI.
    setIsLoading(true);

    try {
      // Fetch meter details for valid MPANs only.
      const results = await searchMultipleMetersByMpan(validMpans);

      // Create failed entries for invalid format MPANs, so they appear in the results error message.
      const invalidResults = invalidMpans.map((mpan) => ({
        mpan,
        success: false,
        isValid: false,
        meterDetails: null,
        error: invalid_mpan_format_error_message,
      }));

      // Combine API results with invalid format entries so all errors are displayed together.
      setMpanResults([...results, ...invalidResults]);
    } catch (error) {
      // Display any unexpected errors from the API call.
      setMpanError("An error occurred while searching. Please try again.");
      console.log("MPAN search error:", error);
    } finally {
      // Reset the loading state once done.
      setIsLoading(false);
    }
  };

  return {
    mpanInput,
    setMpanInput,
    validationError,
    setValidationError,
    mpanError,
    setMpanError,
    isLoading,
    setIsLoading,
    mpanResults,
    setMpanResults,
    handleSearch,
  };
};

export default useMpanSearch;
